/* eslint-disable react-hooks/exhaustive-deps */
import { Box, LinearProgress } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { gql, useQuery, useSubscription } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";
import FlashMessage from "../../components/FlashMessage";
import { Header } from "../../components/Header";
import LiveOrderBanner from "../../components/Orders/LiveOrderBanner";
import OrderHistoryCard from "../../components/Orders/OrderHistoryCard";
import useReorder from "../../components/Orders/useReorder";
import { isPaidDineIn } from "../../components/Orders/OrderDetail/orderProgress";
import { FilterChip } from "../../components/RestaurantList/listUi";
import UserContext from "../../context/User";
import EmbedContext from "../../context/Embed";
import Analytics from "../../utils/analytics";
import { myLoyaltyPoints, myOrdersPage, myOrdersCounts, orderStatusChanged } from "../../apollo/server";
import { ACTIVE_STATUS } from "../../utils/constantValues";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS, TYPE, VISUALLY_HIDDEN, topBarOffset } from "../../utils/designTokens";

const MY_LOYALTY = gql`${myLoyaltyPoints}`;
const ORDERS_PAGE = gql`${myOrdersPage}`;
const COUNTS = gql`${myOrdersCounts}`;
const STATUS_CHANGED = gql`${orderStatusChanged}`;
// Past orders come a page at a time; Show more asks for the next one.
const PAGE = 18;
const FILTERS = [
  { key: "all", label: "All", status: "PAST" },
  { key: "delivered", label: "Delivered", status: "DELIVERED" },
  { key: "cancelled", label: "Cancelled", status: "CANCELLED" },
];

// Same split as the backend (graphql/resolvers/orderList.js).
const isLive = (o) =>
  ACTIVE_STATUS.includes(o.orderStatus) &&
  !(o.orderStatus === "PICKED" && o.isPickedUp) &&
  !isPaidDineIn(o) &&
  !["RESOLVED", "CLOSED_NO_ACTION"].includes(o.supportTicketStatus);

const toDate = (raw) => new Date(/^\d+$/.test(String(raw)) ? parseInt(raw, 10) : raw);

function Stat({ label, value, gold }) {
  return (
    <Box sx={{ px: 2, py: 1.25, borderRadius: "14px", bgcolor: gold ? C.goldSoft : C.surface, minWidth: 92 }}>
      <Box sx={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 600, color: gold ? C.gold : C.muted }}>{label}</Box>
      <Box sx={{ fontFamily: FONT_BODY, fontSize: 18, fontWeight: 800, color: gold ? C.gold : C.ink, lineHeight: 1.25 }}>{value}</Box>
    </Box>
  );
}

function Empty({ title, body, action }) {
  return (
    <Box sx={{ border: `1.5px dashed ${C.borderStrong}`, borderRadius: "22px", px: 3, py: 6, textAlign: "center" }}>
      <Box component="p" sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 17, fontWeight: 700, color: C.ink }}>{title}</Box>
      {body && <Box component="p" sx={{ ...TYPE.body, m: 0, mt: 0.75, color: C.muted }}>{body}</Box>}
      {action}
    </Box>
  );
}

function MyOrders() {
  const [error, setError] = useState({});
  // Set at checkout when an order earned points.
  const [loyaltyMsg, setLoyaltyMsg] = useState(() => {
    const msg = sessionStorage.getItem("loyaltyEarnedMessage");
    if (msg) sessionStorage.removeItem("loyaltyEarnedMessage");
    return msg || null;
  });
  const { isLoggedIn, profile } = useContext(UserContext);
  // Embed mode (partner iframe pinned to one restaurant): only the customer's
  // orders from the pinned restaurant, not their whole history.
  const { isEmbed, embedSlug } = useContext(EmbedContext);
  const restaurantSlug = isEmbed && embedSlug ? embedSlug : null;
  const { data: loyaltyData } = useQuery(MY_LOYALTY, { skip: !isLoggedIn, fetchPolicy: "cache-first" });
  const points = loyaltyData?.myLoyaltyPoints?.balance;
  const { reorder, busyId, dialog } = useReorder();

  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  // The search asks the server once typing pauses.
  const [search, setSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);
  const status = FILTERS.find((f) => f.key === filter).status;

  // Three small requests instead of the whole history: the live orders, the
  // first page of past orders for this filter and search, and the counts.
  const live = useQuery(ORDERS_PAGE, {
    variables: { status: "ACTIVE", offset: 0, limit: 50, restaurantSlug },
    skip: !isLoggedIn,
    fetchPolicy: "cache-and-network",
  });
  const past = useQuery(ORDERS_PAGE, {
    variables: { status, search: search || null, offset: 0, limit: PAGE, restaurantSlug },
    skip: !isLoggedIn,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });
  const counts = useQuery(COUNTS, {
    variables: { status, search: search || null, restaurantSlug },
    skip: !isLoggedIn,
    fetchPolicy: "cache-and-network",
  });

  const activeOrders = (live.data?.orders || []).filter(isLive);
  const pastList = (past.data || past.previousData)?.orders || [];
  const total = counts.data?.total ?? counts.previousData?.total;
  const matching = counts.data?.matching ?? counts.previousData?.matching ?? 0;
  const loadingMore = past.networkStatus === 3;
  const hasMore = pastList.length < matching;
  const firstLoad = (live.loading && !live.data) || (counts.loading && !counts.data && !counts.previousData);

  const showMore = () =>
    past.fetchMore({
      variables: { offset: pastList.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        const more = fetchMoreResult?.orders || [];
        if (!more.length) return prev;
        const seen = new Set((prev?.orders || []).map((o) => String(o._id)));
        return { ...prev, orders: [...(prev?.orders || []), ...more.filter((o) => !seen.has(String(o._id)))] };
      },
    });

  // Live status. Each order is one cache entry (Order:<id>), so a change shows
  // on its card by itself; only an order moving between live and past (or a
  // new one) needs the lists asked for again.
  const liveIdsRef = useRef(new Set());
  liveIdsRef.current = new Set(activeOrders.map((o) => String(o._id)));
  useSubscription(STATUS_CHANGED, {
    variables: { userId: profile?._id },
    skip: !profile?._id,
    onData: ({ data }) => {
      const o = data?.data?.orderStatusChanged?.order;
      if (!o) return;
      if (isLive(o) !== liveIdsRef.current.has(String(o._id))) {
        live.refetch();
        past.refetch();
        counts.refetch();
      }
    },
  });

  // "SEPTEMBER 2026" groups, newest first.
  const months = useMemo(() => {
    const groups = [];
    [...pastList].sort((a, b) => toDate(b.createdAt) - toDate(a.createdAt)).forEach((o) => {
      const d = toDate(o.createdAt);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      let g = groups[groups.length - 1];
      if (!g || g.key !== key) {
        g = { key, label: d.toLocaleDateString("en-US", { month: "long", year: "numeric" }), orders: [] };
        groups.push(g);
      }
      g.orders.push(o);
    });
    return groups;
  }, [pastList]);

  useEffect(() => {
    Analytics.track(Analytics.events.NAVIGATE_TO_ORDER);
  }, []);

  const loadError = live.error || past.error || counts.error;
  useEffect(() => {
    if (loadError) setError({ type: "error", message: loadError.message || "Couldn't load your orders. Please try again." });
  }, [loadError]);

  const toggleSnackbar = useCallback(() => setError({}), []);

  if (firstLoad) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: C.bg }}>
        <Header />
        <LinearProgress color="primary" sx={{ mt: topBarOffset(0) }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: C.bg }}>
      <FlashMessage open={Boolean(error.type)} severity={error.type} alertMessage={error.message} handleClose={toggleSnackbar} />
      <FlashMessage open={Boolean(loyaltyMsg)} severity="success" alertMessage={loyaltyMsg} handleClose={() => setLoyaltyMsg(null)} />
      <Header />

      <Box
        sx={{
          boxSizing: "border-box",
          width: "100%",
          maxWidth: 1120 + 64,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: topBarOffset(20), md: topBarOffset(28) },
          pb: 6,
          display: "flex",
          flexDirection: "column",
          gap: "36px",
        }}
      >
        <Box component="header" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box component="h1" sx={{ ...TYPE.pageTitle, m: 0, color: C.ink }}>My orders</Box>
            <Box component="p" sx={{ m: 0, mt: 0.75, fontFamily: FONT_BODY, fontSize: 15, color: C.muted }}>
              Track what's cooking and reorder your favourites
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1.25 }}>
            {typeof total === "number" && <Stat label="Orders" value={total.toLocaleString()} />}
            {typeof points === "number" && <Stat label="Points" value={points.toLocaleString()} gold />}
          </Box>
        </Box>

        {activeOrders.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {activeOrders.map((o) => (
              <LiveOrderBanner key={o._id} order={o} />
            ))}
          </Box>
        )}

        {total === 0 ? (
          <Empty
            title="No orders yet"
            body="Your orders will show here as soon as you place one."
            action={
              <Box component={RouterLink} to="/restaurant-list" sx={{ ...FOCUS_RING, display: "inline-flex", alignItems: "center", mt: 2.5, height: 44, px: 2.5, borderRadius: RADIUS.pill, bgcolor: C.red, color: "#FFFFFF", fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, textDecoration: "none", "&:hover": { bgcolor: C.redHover } }}>
                Browse restaurants
              </Box>
            }
          />
        ) : (
          <Box component="section" aria-labelledby="past-title">
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", columnGap: 3, rowGap: 1.5, mb: 2.5 }}>
              <Box component="h2" id="past-title" sx={{ ...TYPE.sectionTitle, m: 0, color: C.ink }}>Past orders</Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                <Box role="group" aria-label="Filter past orders" sx={{ display: "flex", gap: 1 }}>
                  {FILTERS.map((f) => (
                    <FilterChip key={f.key} pressed={filter === f.key} onClick={() => setFilter(f.key)}>{f.label}</FilterChip>
                  ))}
                </Box>
                <Box sx={{ position: "relative", width: { xs: "100%", sm: 240 } }}>
                  <Box component="label" htmlFor="orders-search" sx={VISUALLY_HIDDEN}>Search past orders</Box>
                  <SearchRoundedIcon aria-hidden="true" sx={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 20, color: C.muted, pointerEvents: "none" }} />
                  <Box
                    component="input"
                    id="orders-search"
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Restaurant, dish or order #"
                    sx={{ ...FOCUS_RING, boxSizing: "border-box", width: "100%", height: 40, pl: 5, pr: 1.5, borderRadius: RADIUS.pill, border: `1px solid ${C.borderStrong}`, bgcolor: C.bg, color: C.ink, fontFamily: FONT_BODY, fontSize: 14, "&::placeholder": { color: C.muted } }}
                  />
                </Box>
              </Box>
            </Box>

            {pastList.length === 0 && past.loading ? (
              <LinearProgress color="primary" aria-label="Loading past orders" sx={{ borderRadius: 2 }} />
            ) : pastList.length === 0 ? (
              <Empty
                title={filter === "all" && !search ? "No past orders yet" : "No orders match this filter."}
                body={filter === "all" && !search ? "Finished orders will show here." : null}
              />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {months.map((m) => (
                  <Box key={m.key} component="section" aria-label={m.label}>
                    <Box component="h3" sx={{ m: 0, mb: 1.5, fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.muted }}>
                      {m.label}
                    </Box>
                    <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))", gap: "16px" }}>
                      {m.orders.map((o) => (
                        <Box component="li" key={o._id} sx={{ minWidth: 0, display: "flex" }}>
                          <OrderHistoryCard order={o} onReorder={reorder} reordering={busyId === o._id} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
                {hasMore && (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={showMore}
                      disabled={loadingMore}
                      aria-busy={loadingMore}
                      sx={{ ...BUTTON_RESET, ...FOCUS_RING, height: 44, px: 3, borderRadius: RADIUS.pill, border: `1.5px solid ${C.borderStrong}`, color: C.ink, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, "&:hover": { bgcolor: C.surface } }}
                    >
                      {loadingMore ? "Loading…" : `Show more orders (${matching - pastList.length} more)`}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
      {dialog}
    </Box>
  );
}

export default MyOrders;
